(() => {
  'use strict';
  if (customElements.get('tbv-diamond-guide')) return;
  const colorGrades = ['D','E','F','G','H','I','J','K','L','M','N','OP','QR','ST','UV','WX','YZ'];
  const colors = colorGrades.map((grade, i) => {
    const title = i < 3 ? 'Colorless' : i < 7 ? 'Near colorless' : i < 10 ? 'Faint color' : i < 13 ? 'Very light color' : 'Light color';
    const desc = i < 3 ? 'These grades show very little body color. Small differences between D, E and F are assessed under controlled grading conditions.' : i < 7 ? 'A subtle hint of warmth may be visible under close inspection. Appearance also depends on the diamond’s size, shape and setting.' : i < 10 ? 'A faint warm tint may be noticeable, especially in larger diamonds and when viewed from the side.' : i < 13 ? 'A more visible warm tint is present. This illustration shows the progression of body color along the D–Z scale.' : 'A noticeable light yellow or warm tint is present. This guide illustrates the D–Z scale, not fancy-color grading.';
    return [grade,title,desc];
  });
  const grades = {
    color: colors,
    clarity: [
      ['FL','Flawless','No inclusions or blemishes are visible to a skilled grader under 10× magnification.'],
      ['IF','Internally flawless','No inclusions are visible under 10× magnification; minor surface blemishes may be present.'],
      ['VVS1, VVS2','Very very slightly included','Minute inclusions are very difficult for a skilled grader to see under 10× magnification.'],
      ['VS1, VS2','Very slightly included','Minor inclusions range from difficult to somewhat easy to see under 10× magnification. Visibility without magnification varies by diamond.'],
      ['SI1, SI2','Slightly included','Inclusions are noticeable under 10× magnification and may be visible without magnification in some diamonds.'],
      ['I1, I2, I3','Included','Inclusions are obvious under 10× magnification and may affect transparency and brilliance.']
    ],
    cut: [
      ['ID','Ideal','An ideal designation is used by some grading systems for highly precise proportions and light performance. Definitions vary by grading laboratory.'],
      ['EX','Excellent','Excellent cut quality supports strong brightness, fire and scintillation. Consult the grading report for the diamond’s assessed cut grade.'],
      ['VG','Very good','Very good cut quality offers effective light return, with some differences in proportions and visual performance compared with excellent grades.'],
      ['G','Good','Good cut quality returns a substantial amount of light, though some light escapes instead of returning toward the viewer.'],
      ['F','Fair','Fair cut proportions can allow more light to escape, reducing visible brightness and sparkle.'],
      ['P','Poor','Poor cut proportions can allow significant light leakage, resulting in reduced brightness.']
    ],
    carat: [.25,.5,.75,1,1.25,1.5,1.75,2,2.5,3,3.5,4,4.5,5,6,7,8,9,10].map(n => [n.toFixed(2),`${n.toFixed(2)} carat`, 'Carat measures weight, not diameter. One carat equals 0.20 grams. The illustration compares relative size for similar round diamonds; actual dimensions vary with shape and proportions.'])
  };
  const normalize = x => String(x ?? '').trim().toUpperCase();
  const indexFor = (key, raw) => {
    const value = normalize(raw);
    if (!value) return -1;
    if (key === 'carat') {
      // Avoid interpreting ranges or total-weight descriptions as a single stone weight.
      if (!/^\d*\.?\d+\s*(CT|CARAT(S)?)?$/.test(value)) return -1;
      const n = parseFloat(value); if (!(n > 0)) return -1;
      return grades.carat.reduce((best, row, i) => Math.abs(+row[0]-n) < Math.abs(+grades.carat[best][0]-n) ? i : best, 0);
    }
    if (key === 'color') return colorGrades.findIndex(g => g === value || (g.length === 2 && value.length === 1 && g.includes(value)));
    if (key === 'clarity') return grades.clarity.findIndex(row => row[0] === value || row[0].split(', ').includes(value));
    const map = {IDEAL:'ID',EXCELLENT:'EX','VERY GOOD':'VG',GOOD:'G',FAIR:'F',POOR:'P'};
    return grades.cut.findIndex(row => row[0] === (map[value] || value));
  };
  class DiamondGuide extends HTMLElement {
    connectedCallback() {
      if (this.bound) { this.root.addEventListener('change',this.onChange); this.resizeObserver?.observe(this); this.syncVariant(); return; }
      this.bound = true;
      try { this.images = JSON.parse(this.querySelector('[data-guide-images]').textContent); } catch (_) { this.images = {}; }
      this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
      this.groups = [...this.querySelectorAll('[data-guide-group]')];
      this.productValues = Object.fromEntries(Object.keys(grades).map(k => [k,this.dataset[k] || '']));
      try { this.variantData = JSON.parse(this.querySelector('[data-guide-variants]').textContent); } catch (_) { this.variantData = {variants:[]}; }
      this.root = this.closest('.fd-product, .tbv-ring-product') || this.closest('[id^="TBVRingProduct-"]') || this.parentElement;
      this.groups.forEach(group => {
        const key = group.dataset.guideGroup;
        const scale = group.querySelector('.tbv-guide__scale');
        const visual = group.querySelector('.tbv-guide__visual');
        group._frames = [];
        group._visual = visual;
        grades[key].forEach((row, i) => {
          const button = document.createElement('button');
          button.type = 'button'; button.textContent = row[0]; button.dataset.index = i;
          button.setAttribute('aria-label', `${key === 'carat' ? row[1] : row[0] + ': ' + row[1]}`);
          button.setAttribute('aria-pressed','false'); scale.append(button);
        });
        scale.addEventListener('click', e => { const b = e.target.closest('button'); if (group._suppressClick) { e.preventDefault(); return; } if (b) this.select(group,+b.dataset.index,true); });
        scale.addEventListener('keydown', e => {
          const b = e.target.closest('button'); if (!b) return;
          let i = +b.dataset.index;
          if (e.key === 'ArrowRight') i++; else if (e.key === 'ArrowLeft') i--; else if (e.key === 'Home') i=0; else if (e.key === 'End') i=grades[key].length-1; else return;
          e.preventDefault(); i=Math.max(0,Math.min(i,grades[key].length-1)); this.select(group,i,true); scale.children[i].focus({preventScroll:true});
        });
        group.querySelectorAll('[data-guide-step]').forEach(b=>b.addEventListener('click',()=>this.select(group,+group.dataset.index + +b.dataset.guideStep,true)));
        group.querySelector('.tbv-guide__navigation').hidden = false;
        this.bindDrag(group,scale);
      });
      const reset = this.querySelector('.tbv-guide__reset'); reset.hidden=false; reset.addEventListener('click',()=>this.reset());
      this.onChange = () => { // Run after the product form updates its selected variant id.
        clearTimeout(this.changeTimer); this.changeTimer=setTimeout(()=>this.syncVariant(),0);
      };
      this.root.addEventListener('change',this.onChange);
      this.syncVariant();
      this.resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(()=>this.groups.forEach(g=>this.center(g,false))) : null;
      this.resizeObserver?.observe(this);
      this.querySelector('details').addEventListener('toggle',()=>this.groups.forEach(g=>this.center(g,false)));
    }
    disconnectedCallback() { this.root?.removeEventListener('change',this.onChange); this.resizeObserver?.disconnect(); clearTimeout(this.changeTimer);  }
    syncVariant() {
      const id = this.root.querySelector('input[name="id"]')?.value || this.variantData.selected;
      const variant = this.variantData.variants.find(v=>String(v.id)===String(id));
      this.values = Object.fromEntries(Object.keys(grades).map(k=>[k,variant?.[k] || this.productValues[k]]));
      this.reset();
    }
    reset() {
      this.groups.forEach(g=>{
        const key=g.dataset.guideGroup, raw=String(this.values[key] || ''), i=indexFor(key,raw);
        g.querySelector('[data-product-grade]').textContent=raw ? raw+(key==='carat' && !/[a-z]/i.test(raw)?' ct':'') : 'Explore grades';
        g.querySelector('[data-grade-caption]').textContent=raw?'This product':'Grade not provided';
        g.dataset.actualIndex=i;
        this.select(g,i<0?({color:0,clarity:3,cut:1,carat:3}[key]):i,false);
      });
    }
    bindDrag(group,scale) {
      let startX=0,startScroll=0,dragging=false,moved=false;
      const settle=()=>{
        if(!group._manualScroll) return;
        group._manualScroll=false;
        const mid=scale.scrollLeft+scale.clientWidth/2;
        let best=0,distance=Infinity;
        [...scale.children].forEach((b,i)=>{ const d=Math.abs(b.offsetLeft+b.offsetWidth/2-mid); if(d<distance){best=i;distance=d;} });
        this.select(group,best,true);
      };
      scale.addEventListener('pointerdown',e=>{
        group._manualScroll=true;
        if(e.pointerType!=='mouse' || e.button!==0) return;
        dragging=true;moved=false;startX=e.clientX;startScroll=scale.scrollLeft;
      });
      scale.addEventListener('pointermove',e=>{
        if(!dragging) return;
        if(Math.abs(e.clientX-startX)>5){moved=true;group._suppressClick=true;scale.setPointerCapture?.(e.pointerId);}
        if(moved){e.preventDefault();scale.scrollLeft=startScroll-(e.clientX-startX);}
      });
      const end=()=>{
        if(dragging && moved) settle();
        dragging=false;
        clearTimeout(group._clickTimer);group._clickTimer=setTimeout(()=>group._suppressClick=false,80);
      };
      scale.addEventListener('pointerup',end);scale.addEventListener('pointercancel',end);
      scale.addEventListener('lostpointercapture',()=>{dragging=false;});
      scale.addEventListener('wheel',()=>group._manualScroll=true,{passive:true});
      scale.addEventListener('scroll',()=>{
        if(!group._manualScroll) return;
        clearTimeout(group._scrollTimer);
        group._scrollTimer=setTimeout(()=>{if(!dragging)settle();},140);
      },{passive:true});
    }
    center(group,smooth=false) {
      const scale=group.querySelector('.tbv-guide__scale'), b=scale.children[+group.dataset.index];
      if(!b || !scale.clientWidth) return;
      const cell=group.dataset.guideGroup==='clarity'?112:64;
      scale.style.setProperty('--guide-spacer',Math.max(0,(scale.clientWidth-cell)/2)+'px');
      const left=b.offsetLeft-(scale.clientWidth-b.offsetWidth)/2;
      group._manualScroll=false;
      if(scale.scrollTo) scale.scrollTo({left,behavior:smooth&&!this.reducedMotion?'smooth':'instant'});
      else scale.scrollLeft=left;
    }
    paint(group,index,animate) {
      const key=group.dataset.guideGroup, visual=group._visual;
      let frame=group._frames[index];
      if(!frame) {
        frame=document.createElement('div');frame.className='tbv-guide__frame';frame.dataset.frame=index;
        (this.images[key]?.[index] || []).forEach((src,i)=>{
          const img=document.createElement('img');img.src=src;img.alt='';img.decoding='async';
          img.className=i===0&&this.images[key][index].length===2?'tbv-guide__diagram-image':'tbv-guide__photo';
          frame.append(img);
        });
        visual.append(frame);group._frames[index]=frame;
      }
      const token=(group._paintToken||0)+1;group._paintToken=token;
      const reveal=()=>{
        if(token!==group._paintToken || !this.isConnected) return;
        const failed=[...frame.querySelectorAll('img')].some(img=>img.complete&&img.naturalWidth===0);
        if(failed){visual.dataset.imageError='true';return;}
        delete visual.dataset.imageError;
        visual.classList.toggle('tbv-guide__visual--instant',!animate||this.reducedMotion);
        void frame.offsetWidth; // Establish the incoming frame before starting the crossfade.
        group._frames.forEach(f=>{if(f)f.classList.toggle('is-active',f===frame);});
        visual.dataset.imageIndex=String(index);
      };
      // Keep the current photo visible until both incoming assets have decoded.
      const imgs=[...frame.querySelectorAll('img')];
      if(imgs.every(img=>img.complete&&img.naturalWidth>0)) reveal();
      else Promise.all(imgs.map(img=>typeof img.decode==='function'?img.decode().catch(()=>{}):new Promise(resolve=>{img.onload=resolve;img.onerror=resolve;}))).then(reveal);
      // Cache neighbouring photos without fetching any third-party resources.
      for(const neighbour of [index-1,index+1]) {
        for(const src of this.images[key]?.[neighbour]||[]) {
          this._preloaded ||= new Set(); if(!this._preloaded.has(src)){this._preloaded.add(src);const img=new Image();img.src=src;}
        }
      }
    }
    select(group,index,interaction) {
      const key=group.dataset.guideGroup, rows=grades[key]; index=Math.max(0,Math.min(index,rows.length-1));
      group.dataset.index=index;
      const row=rows[index];
      group.querySelectorAll('.tbv-guide__scale>button').forEach((b,i)=>b.setAttribute('aria-pressed',String(i===index)));
      group.querySelector('[data-guide-step="-1"]').disabled=index===0;
      group.querySelector('[data-guide-step="1"]').disabled=index===rows.length-1;
      group.querySelector('[data-guide-title]').textContent=key==='carat'?row[1]:`${row[0]} · ${row[1]}`;
      group.querySelector('[data-guide-description]').textContent=row[2];
      const caption=group.querySelector('[data-grade-caption]');
      if(interaction) caption.textContent=(this.values[key]?'This product · ':'')+`Exploring ${row[0]}${key==='carat'?' ct':''}`;
      else if(+group.dataset.actualIndex<0) caption.textContent=(this.values[key]?'This product · ':'Grade not provided · ')+`Guide example: ${row[0]}${key==='carat'?' ct':''}`;
      else if(key==='carat' && parseFloat(this.values[key])!==+row[0]) caption.textContent=`This product · nearest illustration: ${row[0]} ct`;
      this.paint(group,index,interaction);
      this.center(group,interaction);
    }
  }
  customElements.define('tbv-diamond-guide',DiamondGuide);
})();
